// Service API publique du portail METERIS pour la prise de rendez-vous.
// Backend cible : https://support.meteris.fr (CORS deja autorise pour meteris.fr).
import { fetchJsonWithRetry } from './httpRetry';

const API_BASE = process.env.REACT_APP_PORTAIL_API_URL || 'https://support.meteris.fr';

// Registre #20266 (51f20d33) : un 502/504 du proxy part SANS en-tete CORS, fetch()
// leve « Failed to fetch ». Les GET sont retentes (httpRetry, copie de Meaux) ;
// les POST ne le sont JAMAIS (un 504 peut arriver APRES la creation du RDV) : on
// remplace seulement le message brut du navigateur par une consigne lisible.
const MESSAGE_POST_INCERTAIN =
  "La demande n'a pas pu être confirmée. Vérifiez votre boîte mail avant de réessayer, ou appelez le 01 83 62 33 27.";
const MESSAGE_ANNULATION_INCERTAINE =
  "L'annulation n'a pas pu être confirmée. Merci de réessayer dans un instant, ou appelez le 01 83 62 33 27.";

async function postSansReprise(url, init, messageSiReseau) {
  try {
    return await fetch(url, init);
  } catch (e) {
    if (e instanceof TypeError) throw new Error(messageSiReseau);
    throw e;
  }
}

// Audience B2B : filtre les types de RDV adaptés aux professionnels (ex: comptoir seul).
const BOOKING_AUDIENCE = 'b2b';

// Formate une Date en YYYY-MM-DD via les getters LOCAUX. Ne jamais passer par
// toISOString() pour une date calendaire : minuit Paris devient la veille en
// UTC, ce qui excluait le dernier jour du mois de la requête availability.
const fmtDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Bornes calendaires (locales) du mois contenant `monthDate`.
export function monthRange(monthDate) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return { from: fmtDay(new Date(y, m, 1)), to: fmtDay(new Date(y, m + 1, 0)) };
}

export async function fetchAvailability(fromDate, toDate, typeAlias = 'comptoir', lang, durationMin = null) {
  // Phase 4.1 (2026-05-22) : durationMin propage la duree choisie pour les
  // types `allow_custom_duration` (ex: personnalise). Le backend genere la
  // grid de slots avec cette duree (slot agrege).
  const params = new URLSearchParams({ from: fromDate, to: toDate, type: typeAlias });
  if (lang) params.set('lang', lang);
  if (durationMin) params.set('duration_min', String(durationMin));
  try {
    return await fetchJsonWithRetry(`${API_BASE}/api/public/it-bookings/availability?${params}`);
  } catch (e) {
    if (e.status && e.status < 500) throw new Error('Erreur disponibilités');
    throw e;
  }
}

export async function fetchTypes() {
  try {
    return await fetchJsonWithRetry(
      `${API_BASE}/api/public/it-bookings/types?audience=${BOOKING_AUDIENCE}`,
    );
  } catch {
    return { types: [] };
  }
}

export async function lookupSiret(siret) {
  try {
    const r = await fetch(`${API_BASE}/api/public/it-bookings/sirene-lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siret }),
    });
    if (!r.ok) return { found: false };
    return r.json();
  } catch {
    return { found: false };
  }
}

export async function createBooking(payload) {
  const r = await postSansReprise(
    `${API_BASE}/api/public/it-bookings`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    MESSAGE_POST_INCERTAIN,
  );
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data.detail || 'Erreur lors de la réservation');
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function getCancelInfo(token) {
  try {
    return await fetchJsonWithRetry(
      `${API_BASE}/api/public/it-bookings/cancel-info?token=${encodeURIComponent(token)}`,
    );
  } catch (e) {
    if (e.status && e.status < 500) throw new Error('Token inconnu ou expiré');
    throw e;
  }
}

export async function cancelBooking(token) {
  const r = await postSansReprise(
    `${API_BASE}/api/public/it-bookings/cancel`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    },
    MESSAGE_ANNULATION_INCERTAINE,
  );
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || 'Annulation échouée');
  }
  return r.json();
}
